import axios from 'axios';

/**
 * Client half of MEDIA_UPLOAD_CLIENT_CONTRACT v1 (bounded private media intake).
 *
 * What this module owns, and why it is the ONLY place that owns it:
 *
 *   • The 4-step protocol: grant → bytes (SERVER_INTAKE or presigned PUT) →
 *     finalize → the EXISTING writer endpoint, called with `mediaId`/`mediaIds`
 *     instead of multipart. Nothing here invents an endpoint.
 *   • Feature discovery. The grant call IS the probe (§1), and exactly ONE
 *     answer may select legacy: a 404 whose body code is exactly
 *     `MEDIA_DIRECT_UPLOAD_DISABLED` — this deployment does not have the
 *     feature. That answer is cached PER SIGNED-IN SESSION (token + active
 *     branch), never module-globally, so the next person on this browser, or the
 *     same person after a logout or a tenant switch, probes again from cold.
 *     There is no page-load probe, no poll, no background refresh.
 *   • What is NEVER a compat signal. A 401, a 403 (no `media.upload`), a network
 *     failure, an arbitrary 404 from anything else on the path, or a rejection
 *     after bytes were sent can never silently re-send those bytes down the
 *     legacy path — `MediaUploadError.fallbackToLegacy` is true for the one
 *     discovery outcome above, plus an operator-set `MEDIA_COMPAT_LEGACY` flag
 *     for a deployment PROVEN (out of band, by the operator) to predate the
 *     route. Everything else surfaces as an error. A permission problem is a
 *     permission problem: falling back would turn "you may not upload" into
 *     "you may upload unvalidated", which is the bypass this work closes.
 *   • Bounded work. One request per stage, files sequential, and the status
 *     endpoint is touched ONLY by the `VALIDATION_IN_PROGRESS` backoff (§4.3:
 *     500 ms → 1 s → 2 s, capped at 3 attempts).
 *   • Secrets. A grant's `uploadUrl` is used once, in memory, and never logged,
 *     stored or echoed into an error. No bucket, key, path or credential is
 *     ever persisted client-side, and a finalized `mediaId` is the caller's to
 *     hold in component state until the writer answers (§6).
 *   • Session ownership. Every upload fingerprints (token, active branch) at
 *     start and re-checks before each stage; a logout or a branch change aborts
 *     the in-flight stages instead of attaching media under someone else's
 *     session. `releaseUploadSession()` also revokes every object URL the
 *     module created.
 *
 * Reading media back is deliberately NOT here (§9): every screen this lane
 * touched renders from the record's existing fields, and not-yet-saved files
 * are previewed from local object URLs, so the authorized read endpoint has no
 * caller. Display URLs from a record go through `isSafeMediaUrl` before they
 * reach a `src`/`window.open` — a server string can never smuggle in
 * `javascript:`/`data:`/a `blob:` it did not create.
 */

// Restated per-kind ceilings (contract §3) — the pre-existing 5/10/16 MiB caps,
// never one unified number. whatsapp_media has no portal writer yet, so it is
// not offered to callers.
export const MEDIA_KIND_LIMITS = {
  photo: {
    maxBytes: 5 * 1024 * 1024,
    mimes: ['image/jpeg', 'image/png', 'image/webp'],
  },
  document: {
    maxBytes: 10 * 1024 * 1024,
    mimes: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
  },
};

// mime ⇄ extension must agree exactly (contract §3) — `.jpeg` is NOT accepted
// for image/jpeg, so it is normalized to `.jpg` before a grant is requested.
const EXT_FOR_MIME = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
};

const GRANT_URL = '/api/media/upload-grants';
const MEDIA_ID_RE = /^md_[0-9a-f]{24}$/;

export function isFinalizedMediaId(value) {
  return typeof value === 'string' && MEDIA_ID_RE.test(value);
}

export function normalizeUploadName(name) {
  const raw = String(name || '');
  return raw.replace(/\.jpe?g$/i, '.jpg');
}

/**
 * A failure the caller is expected to present. `fallbackToLegacy` is true ONLY
 * for the feature-dark discovery outcome (or an operator-proven legacy-only
 * deployment) — never for a failure that happened after bytes or a mediaId were
 * in play, and never for a permission denial.
 */
export class MediaUploadError extends Error {
  constructor(code, { fallbackToLegacy = false, mediaId = null, cause } = {}) {
    super(code);
    this.name = 'MediaUploadError';
    this.code = code;
    this.fallbackToLegacy = fallbackToLegacy;
    this.mediaId = mediaId;
    this.cause = cause;
  }
}

// ── session cache + in-flight registry ──────────────────────────────────────

const session = {
  // Capability per signed-in session, keyed by `sessionFingerprint()` — NEVER a
  // module-global boolean, or the second person on this browser (or the first
  // one back after a tenant switch) would inherit the first one's answer.
  // Absent = not yet determined for that session.
  availability: new Map(),
  uploads: new Set(), // AbortControllers of in-flight uploads
  objectUrls: new Set(), // object URLs this module created
};

export function directUploadAvailability() {
  return session.availability.get(sessionFingerprint());
}

function markAvailability(available) {
  session.availability.set(sessionFingerprint(), available);
  return available;
}

/** Per-stage abort: cancels this upload's outstanding request only. */
function register(controller) {
  session.uploads.add(controller);
}
function unregister(controller) {
  session.uploads.delete(controller);
}

/**
 * Abort every in-flight upload and revoke every object URL this module created.
 * Called on unmount by `useMediaUploadSession`. Deliberately does NOT touch the
 * capability cache: the signed-in session continues after one form goes away,
 * and §1 prices the probe at one failed call per session.
 */
export function releaseUploadSession() {
  for (const controller of session.uploads) {
    try {
      controller.abort();
    } catch {
      /* already aborted */
    }
  }
  session.uploads.clear();
  revokeTrackedObjectUrls();
}

/**
 * End-of-session teardown for the auth store: everything `releaseUploadSession`
 * does, plus the capability cache — ALL keys, not just the current one, because
 * a logout or a tenant switch must leave nothing for the next identity on this
 * browser to inherit. Safe to call repeatedly.
 */
export function resetMediaUploadSession() {
  releaseUploadSession();
  session.availability.clear();
}

// ── object URLs ──────────────────────────────────────────────────────────────

/**
 * Create an object URL this module owns. Anything rendered from a LOCAL file
 * must go through here so `releaseUploadSession` can revoke it; a URL string
 * that arrived from a server never goes in this set.
 */
export function createTrackedObjectUrl(blob) {
  const url = window.URL.createObjectURL(blob);
  session.objectUrls.add(url);
  return url;
}

export function revokeTrackedObjectUrl(url) {
  if (!session.objectUrls.delete(url)) return false;
  window.URL.revokeObjectURL(url);
  return true;
}

export function revokeTrackedObjectUrls() {
  for (const url of session.objectUrls) window.URL.revokeObjectURL(url);
  session.objectUrls.clear();
}

const BLOCKED_URL_SCHEMES = /^(?:javascript|data|vbscript|file):/i;

/**
 * Guard for media URLs that came OUT of a record (a photo/document field).
 * Relative and absolute http(s) paths are the only server-renderable shapes;
 * `blob:` is refused unless THIS module created it, so a stored or injected
 * blob/javascript string can never become a `src` or a `window.open` target.
 */
export function isSafeMediaUrl(url) {
  if (typeof url !== 'string' || url.trim() === '') return false;
  const trimmed = url.trim();
  if (session.objectUrls.has(trimmed)) return true; // locally created
  if (BLOCKED_URL_SCHEMES.test(trimmed)) return false;
  if (trimmed.startsWith('blob:')) return false; // not one of ours
  if (trimmed.startsWith('/')) return true; // same-origin API/static path
  return /^https?:\/\//i.test(trimmed);
}

/** The one absolute form the server writes into `file_path` (media contract). */
const HR_MEDIA_PATH_RE = /^\/api\/media\/assets\/md_[0-9a-f]{24}\/download$/;

/**
 * Build the SAME-ORIGIN request URL for an HR document's stored `file_path`.
 *
 * `EmployeeDocument.file_path` holds one of exactly two shapes:
 *
 *   • legacy, RELATIVE to the backend root — "uploads/documents/hr/<co>/x.pdf",
 *     written by `path.relative(BACKEND_ROOT, …)` and served by the Bearer-gated
 *     /uploads mount. It needs a leading "/" to become a request URL.
 *   • media-backed, already ABSOLUTE — "/api/media/assets/md_<24hex>/download".
 *     Prefixing "/" onto it yields "//api/media/..." — a PROTOCOL-RELATIVE URL
 *     that the browser resolves to a host literally named "api". That request
 *     never reaches the backend, which is the defect this exists to fix.
 *
 * Returns `null` for anything that is not one of those two shapes, and the
 * caller must treat `null` as "cannot view" rather than falling back.
 *
 * WHY NOT `startsWith('/')` ALONE (the minimal fix the contract suggests):
 * this URL is fetched by axios WITH the Authorization header attached, so a
 * value that leaves the origin leaks the caller's bearer token to whatever host
 * it names. `startsWith('/')` accepts "//evil.example/x" and the backslash twin
 * "/\evil.example/x" ("\" folds to "/" in the WHATWG parser), both of which are
 * off-origin. Neither is a shape the server ever writes, so refusing them costs
 * nothing and closes a credential-exfiltration path on a stored, mutable field.
 * `isSafeMediaUrl` is not the right guard here either — it deliberately allows
 * absolute http(s) for render sinks, which is exactly what must not happen to
 * an authenticated fetch.
 */
export function hrDocumentUrl(filePath) {
  if (typeof filePath !== 'string') return null;
  const raw = filePath.trim();
  if (raw === '') return null;
  // Control characters make scheme/host parsing browser-dependent.
  // eslint-disable-next-line no-control-regex -- matching control characters is this check's whole job
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  // A backslash anywhere is not a shape the server writes, and it folds to "/".
  if (raw.includes('\\')) return null;
  if (raw.includes('..')) return null; // no traversal, in either shape

  if (raw.startsWith('/')) {
    // Absolute: the ONLY absolute form the server writes is the media URL, and
    // it is matched exactly — anchored, with the same md_ + 24 hex the backend
    // enforces. "//host" and "/\host" cannot match it.
    return HR_MEDIA_PATH_RE.test(raw) ? raw : null;
  }
  // Relative legacy path. It must not carry a scheme or an authority of its own
  // ("https://evil/x", "evil.example/x" is fine as a path but a scheme is not).
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
  return `/${raw}`;
}

// ── session ownership ────────────────────────────────────────────────────────

export function sessionFingerprint() {
  return `${localStorage.getItem('token') || ''}|${localStorage.getItem('activeGarageId') || ''}`;
}

/** True once the signed-in identity or the active branch changed since `fp`. */
export function sessionChangedSince(fp) {
  return sessionFingerprint() !== fp;
}

function throwIfSessionChanged(fp) {
  if (sessionChangedSince(fp)) throw new MediaUploadError('SESSION_CHANGED');
}

// ── preflight ────────────────────────────────────────────────────────────────

function extensionOf(name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ''));
  return m ? `.${m[1].toLowerCase()}` : '';
}

/**
 * The cheap client-side half of the server's grant validation: the declared
 * mime must be allowed for the kind, agree with the (normalized) extension,
 * and fit the kind's ceiling. Returns a stable code so the caller can show a
 * localized message without a round-trip.
 */
export function preflightFile(file, kind) {
  const limits = MEDIA_KIND_LIMITS[kind];
  if (!limits) return { ok: false, code: 'UNSUPPORTED_RESOURCE_KIND' };
  if (!file) return { ok: false, code: 'INVALID_REQUEST' };
  const mime = file.type;
  if (!limits.mimes.includes(mime)) return { ok: false, code: 'UNSUPPORTED_FILE_TYPE' };
  if (!Number(file.size)) return { ok: false, code: 'EMPTY_FILE' };
  const name = normalizeUploadName(file.name);
  if (EXT_FOR_MIME[mime] !== extensionOf(name)) return { ok: false, code: 'TYPE_MISMATCH' };
  if (Number(file.size) > limits.maxBytes) return { ok: false, code: 'TOO_LARGE' };
  return { ok: true, name, mime };
}

// i18n keys per stable code (see src/i18n.js `mediaUpload.*`). Codes that mean
// "client bug" share the generic sentence: they are not user-recoverable detail.
const ERROR_KEY_BY_CODE = {
  FORBIDDEN: 'mediaUpload.forbidden',
  UNSUPPORTED_FILE_TYPE: 'mediaUpload.unsupportedType',
  TYPE_MISMATCH: 'mediaUpload.typeMismatch',
  TOO_LARGE: 'mediaUpload.tooLarge',
  QUOTA_EXCEEDED: 'mediaUpload.quotaExceeded',
  GRANT_EXPIRED: 'mediaUpload.grantExpired',
  NOT_UPLOADED: 'mediaUpload.notUploaded',
  VALIDATION_IN_PROGRESS: 'mediaUpload.stillProcessing',
  MEDIA_NOT_READY: 'mediaUpload.notReady',
  PDF_ENCRYPTED: 'mediaUpload.pdfEncrypted',
  PDF_PAGES_EXCEEDED: 'mediaUpload.pdfPages',
  PDF_MALFORMED: 'mediaUpload.pdfMalformed',
  SESSION_CHANGED: 'mediaUpload.sessionChanged',
  NETWORK: 'mediaUpload.failed',
  EMPTY_FILE: 'mediaUpload.emptyFile',
};

export function mediaUploadErrorKey(error) {
  return ERROR_KEY_BY_CODE[error?.code] || 'mediaUpload.failed';
}

// ── the protocol ─────────────────────────────────────────────────────────────

function aborted(err) {
  return axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

/**
 * The ONLY client-side legacy selector (leader correction, overriding contract
 * §1's 403 clause): a 404 whose body code is EXACTLY the feature-dark signal.
 * Everything else on this endpoint — 401, 403, a network failure, a 404 carrying
 * any other code or none (a proxy, an old server answering its own 404 page) —
 * is an error. A permission denial must never become an unvalidated re-send,
 * and an arbitrary 404 proves nothing about the feature.
 */
function isFeatureDark(status, code) {
  return status === 404 && code === 'MEDIA_DIRECT_UPLOAD_DISABLED';
}

/**
 * Out-of-band operator opt-out, for a deployment PROVEN (by the operator, e.g.
 * from the deployed server's own version) to predate the route entirely. It is
 * never inferred from a response here — an inference is exactly how a 403 turns
 * into an authorization bypass.
 */
function legacyCompatDeclared() {
  try {
    return window?.MEDIA_COMPAT_LEGACY === true;
  } catch {
    return false;
  }
}

async function requestGrant(file, { kind, resourceId, signal, fp }) {
  const pre = preflightFile(file, kind);
  if (!pre.ok) throw new MediaUploadError(pre.code);
  throwIfSessionChanged(fp);

  const body = {
    resourceKind: kind,
    originalName: pre.name,
    declaredMime: pre.mime,
    declaredBytes: file.size,
  };
  // Only sent when there IS a record to bind to (contract §3). Every writer in
  // §7 has one; a grant without a resourceId cannot be spent on any record.
  if (resourceId !== undefined && resourceId !== null && resourceId !== '') {
    body.resourceId = resourceId;
  }

  try {
    const { data } = await axios.post(GRANT_URL, body, { signal });
    // Discovery: the feature is live for this session.
    markAvailability(true);
    if (!data?.grant?.uploadId) throw new MediaUploadError('INVALID_REQUEST');
    return data.grant;
  } catch (err) {
    if (aborted(err)) throw new MediaUploadError('ABORTED', { cause: err });
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    if (err && !err.response) throw new MediaUploadError('NETWORK', { cause: err });
    if (status === 401) throw new MediaUploadError('UNAUTHENTICATED', { cause: err });
    if (status === 403) {
      // No `media.upload`. This is an authorization DECISION, not a capability
      // answer: it is surfaced, it is not cached as "unavailable", and it never
      // selects the legacy multipart path (which would bypass the validation
      // the caller is not entitled to skip).
      throw new MediaUploadError('FORBIDDEN', { cause: err });
    }
    if (isFeatureDark(status, code) || legacyCompatDeclared()) {
      // The one sanctioned fallback. Cached per session, so this exact cost is
      // paid once per signed-in session.
      markAvailability(false);
      throw new MediaUploadError(code || 'MEDIA_DIRECT_UPLOAD_DISABLED', {
        fallbackToLegacy: true,
        cause: err,
      });
    }
    // A real refusal — including any other 404. Never a licence to re-send the
    // bytes unvalidated.
    throw new MediaUploadError(code || 'INVALID_REQUEST', { cause: err });
  }
}

/** One request, the grant's own way: our intake, or the provider's presigned PUT. */
async function sendBytes(grant, file, { mime, signal }) {
  const contentType = grant.requiredHeaders?.['Content-Type'] || mime;
  if (grant.method === 'SERVER_INTAKE') {
    // Our own endpoint: same axios instance, so auth/branch headers ride along.
    // `uploadUrl` IS this path (the router hands it back for SERVER_INTAKE); it
    // is only followed when it is same-origin relative, never when a malformed
    // grant tries to point the bytes somewhere else.
    const sameOrigin = typeof grant.uploadUrl === 'string' && grant.uploadUrl.startsWith('/');
    const path = sameOrigin ? grant.uploadUrl : `/api/media/uploads/${grant.uploadId}/binary`;
    try {
      await axios.post(path, file, {
        headers: { 'Content-Type': contentType },
        signal,
      });
    } catch (err) {
      if (aborted(err)) throw new MediaUploadError('ABORTED', { cause: err });
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (err && !err.response) throw new MediaUploadError('NETWORK', { cause: err });
      if (status === 401) throw new MediaUploadError('UNAUTHENTICATED', { cause: err });
      if (status === 403) throw new MediaUploadError('FORBIDDEN', { cause: err });
      throw new MediaUploadError(code || 'NOT_UPLOADED', { cause: err });
    }
    return;
  }
  if (grant.method === 'PUT') {
    // Presigned: an unsigned header breaks the signature, and the browser sets
    // Content-Length itself. Deliberately NOT axios — no session auth header
    // may leak to object storage, and the URL is single-purpose and expiring.
    if (!/^https:\/\//i.test(grant.uploadUrl)) throw new MediaUploadError('INVALID_REQUEST');
    const res = await fetch(grant.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
      signal,
    });
    if (!res.ok) throw new MediaUploadError('NOT_UPLOADED');
    return;
  }
  throw new MediaUploadError('INVALID_REQUEST');
}

/**
 * Finalize. A 400 carrying a `result` body is the server's rejection of the
 * actual bytes: the grant is spent, the code is stable, and the caller shows
 * it — never a legacy re-send (§4.3).
 */
async function finalizeUpload(grant, { fp, signal }) {
  throwIfSessionChanged(fp);
  let res;
  try {
    res = await axios.post(`/api/media/uploads/${grant.uploadId}/finalize`, {}, { signal });
  } catch (err) {
    if (aborted(err)) throw new MediaUploadError('ABORTED', { cause: err });
    const status = err?.response?.status;
    const body = err?.response?.data;
    if (status === 401) throw new MediaUploadError('UNAUTHENTICATED', { cause: err });
    if (err && !err.response) throw new MediaUploadError('NETWORK', { cause: err });
    // VALIDATION_IN_PROGRESS is the one retryable answer — handled by the
    // caller's bounded backoff, which is the only sanctioned status poll.
    throw new MediaUploadError(body?.code || body?.result?.reason || 'INVALID_REQUEST', {
      cause: err,
    });
  }
  const result = res.data?.result;
  if (res.data?.success && result?.state === 'ready' && isFinalizedMediaId(result.mediaId)) {
    return result.mediaId;
  }
  throw new MediaUploadError(result?.reason || res.data?.code || 'INVALID_REQUEST');
}

/**
 * Finalize with §4.3's backoff: 500 ms → 1 s → 2 s, one status read between
 * attempts, capped at 3 — and then it gives up rather than poll forever.
 */
async function finalizeWithBackoff(grant, { fp, signal }) {
  const backoff = [500, 1000, 2000];
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await finalizeUpload(grant, { fp, signal });
    } catch (err) {
      if (err.code !== 'VALIDATION_IN_PROGRESS' || attempt >= backoff.length) throw err;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, backoff[attempt]));
      if (signal?.aborted) throw new MediaUploadError('ABORTED');
      throwIfSessionChanged(fp);
      // eslint-disable-next-line no-await-in-loop
      const { data } = await axios.get(`/api/media/uploads/${grant.uploadId}`, { signal });
      const state = data?.result?.state;
      if (state === 'ready' && isFinalizedMediaId(data.result.mediaId)) return data.result.mediaId;
      if (state === 'rejected') throw new MediaUploadError(data.result?.reason || 'INVALID_REQUEST');
      if (state === 'pending' || state === 'validating') continue; // bounded by the loop
      throw new MediaUploadError(data?.result?.reason || data?.result?.code || 'CONFLICT');
    }
  }
}

/**
 * Steps 1–3 for ONE file: grant → bytes → finalize → `mediaId`.
 *
 * @param {File|Blob} file
 * @param {object} opts
 * @param {'photo'|'document'} opts.kind        contract §3 kind
 * @param {string} [opts.resourceId]            the record being attached to
 * @param {AbortSignal} [opts.signal]           the caller's own cancellation
 * @param {(stage: string) => void} [opts.onStage]  'grant'|'bytes'|'finalize'
 * @returns {Promise<string>} the finalized mediaId
 */
export async function uploadOne(file, { kind, resourceId, signal, onStage, onGrant } = {}) {
  const fp = sessionFingerprint();
  const controller = new AbortController();
  register(controller);
  // Either signal aborts: the caller's (component unmount / cancel button), or
  // the session owner's (logout / branch change via releaseUploadSession).
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort);
  const mime = file.type;
  const stage = (s) => onStage?.(s);
  try {
    stage('grant');
    const grant = await requestGrant(file, {
      kind,
      resourceId,
      signal: controller.signal,
      fp,
    });
    // The grant's own session id — handed to the caller so it can abort (§4.4)
    // a partly-finished batch; mediaId alone cannot cancel anything.
    if (grant.uploadId) onGrant?.(grant.uploadId);
    stage('bytes');
    await sendBytes(grant, file, { mime, signal: controller.signal });
    stage('finalize');
    const mediaId = await finalizeWithBackoff(grant, {
      fp,
      signal: controller.signal,
      onStage,
    });
    throwIfSessionChanged(fp); // never hand back an id owned by a dead session
    return mediaId;
  } finally {
    signal?.removeEventListener('abort', onExternalAbort);
    unregister(controller);
  }
}

/**
 * Cancel an in-flight `uploadOne` cleanly — best-effort server-side abort so
 * the tenant reservation is freed now rather than at grant expiry (§4.4).
 * `uploadId` is optional: when the grant has not answered yet there is nothing
 * to abort server-side, and expiry handles it.
 */
export async function cancelUpload(uploadId, { signal } = {}) {
  // `uploadId` is the grant's opaque session id, not a mediaId: no format
  // check beyond "present".
  if (!uploadId) return;
  try {
    await axios.post(`/api/media/uploads/${uploadId}/abort`, {}, { signal });
  } catch {
    /* expiry is the safety net; a failed abort is never surfaced */
  }
}

// ── steps 1–4 combined, per writer shape (contract §7) ───────────────────────

/**
 * Multi-file writer (rows 1–5): one grant+upload+finalize PER FILE, all with
 * the same `resourceId`, then ONE JSON writer call carrying every mediaId.
 * All-or-nothing is the server's rule, so nothing is sent until every file is
 * finalized.
 */
export async function attachMultipleFiles(
  files,
  { kind, resourceId, endpoint, method = 'post', fields = {}, signal, onFile, onStage }
) {
  const mediaIds = [];
  const uploadIds = []; // grant session ids, for the server-side abort
  try {
    for (const file of files) {
      onFile?.(file);
      const mediaId = await uploadOne(file, {
        kind,
        resourceId,
        signal,
        onStage,
        onGrant: (id) => uploadIds.push(id),
      });
      mediaIds.push(mediaId);
    }
  } catch (err) {
    // Abandon what was uploaded so no reservation outlives the attempt.
    await Promise.all(uploadIds.map((id) => cancelUpload(id, { signal }).then(() => {}, () => {})));
    throw err;
  }
  try {
    const { data } = await (method === 'put' ? axios.put : axios.post)(
      endpoint,
      { ...fields, mediaIds },
      { signal }
    );
    return data;
  } catch (err) {
    if (aborted(err)) throw new MediaUploadError('ABORTED', { cause: err });
    throw err;
  }
}

/**
 * Single-file writer (rows 6–9): steps 1–3, then ONE JSON writer call with
 * `mediaId` plus exactly the non-file fields the multipart form takes today.
 *
 * Pass `mediaId` back in after a writer failure to retry step 4 only — the
 * bytes were already accepted, so a retry cannot upload a duplicate (§4.3).
 *
 * Every row here, row 9 included, attaches to a record and so REQUIRES
 * `resourceId`: row 9 is the job-card expense receipt, whose grant is bound to
 * the job card the writer re-checks it against. Omitting it does not degrade
 * gracefully — the server refuses a bound grant spent without a resourceId, and
 * refuses an unbound grant spent on a record, both with RESOURCE_MISMATCH. The
 * flow that genuinely has no target record is the standalone document upload
 * (`/api/upload/expense-document`), which does not go through this function.
 *
 * The writer's response body is returned unchanged, so the caller's success
 * handling is identical for both paths.
 */
export async function attachSingleFile(
  file,
  {
    kind,
    resourceId,
    endpoint,
    method = 'post',
    fields = {},
    mediaId: reuseMediaId,
    signal,
    onStage,
  } = {}
) {
  let mediaId = reuseMediaId;
  if (!isFinalizedMediaId(mediaId)) {
    mediaId = await uploadOne(file, { kind, resourceId, signal, onStage });
  }
  try {
    const { data } = await (method === 'put' ? axios.put : axios.post)(
      endpoint,
      { ...fields, mediaId },
      { signal }
    );
    return { data, mediaId };
  } catch (err) {
    if (aborted(err)) throw new MediaUploadError('ABORTED', { cause: err });
    // The id survives the failure so the caller's retry skips steps 1–3.
    err.mediaId = mediaId;
    throw err;
  }
}
